import { FormEvent, useEffect, useRef, useState } from "react"
import { t } from "../../i18n"
import { ManagementCompany } from "../../Interfaces"
import { CompanyCreateError, managementCompanyService } from "../../services/managementCompanyService"

export default function AddCompanyModal({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean
  onClose: () => void
  onCreated: (company: ManagementCompany) => void
}) {
  const [name, setName] = useState("")
  const [code, setCode] = useState("")
  const [nameError, setNameError] = useState("")
  const [codeError, setCodeError] = useState("")
  const [formError, setFormError] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isOpen) return
    setName("")
    setCode("")
    setNameError("")
    setCodeError("")
    setFormError("")
    setSubmitting(false)
    const id = window.setTimeout(() => nameRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) {
        onClose()
      }
    }

    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [isOpen, onClose, submitting])

  if (!isOpen) return null

  const handleBackdrop = () => {
    if (!submitting) onClose()
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const trimmedName = name.trim()
    const trimmedCode = code.trim()
    const nextNameError = trimmedName ? "" : t("companies.nameRequired")
    const nextCodeError = trimmedCode ? "" : t("companies.codeRequired")
    setNameError(nextNameError)
    setCodeError(nextCodeError)
    setFormError("")
    if (nextNameError || nextCodeError) return

    setSubmitting(true)
    try {
      const company = await managementCompanyService.createFromNameAndCode({
        name: trimmedName,
        code: trimmedCode,
      })
      onCreated(company)
      onClose()
    } catch (error) {
      if (error instanceof CompanyCreateError && error.field === "name") {
        setNameError(t("companies.nameRequired"))
      } else if (error instanceof CompanyCreateError && error.field === "code") {
        setCodeError(error.status === 409 ? t("companies.codeDuplicate") : t("companies.codeRequired"))
      } else {
        setFormError(t("companies.submitFailed"))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/50"
        onClick={handleBackdrop}
        disabled={submitting}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-company-title"
          className="relative w-full max-w-lg rounded-2xl bg-white shadow-xl p-6 md:p-8 font-source"
        >
          <h2 id="add-company-title" className="font-oswald text-3xl font-bold text-primary-dark">
            {t("companies.modalTitle")}
          </h2>
          <p className="mt-2 text-gray-600">{t("companies.modalHelper")}</p>

          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="company-name" className="block font-semibold text-primary-dark mb-1">
                {t("companies.nameLabel")}
              </label>
              <input
                ref={nameRef}
                id="company-name"
                name="name"
                value={name}
                disabled={submitting}
                onChange={(event) => {
                  setName(event.target.value)
                  if (nameError) setNameError("")
                }}
                className="w-full rounded-xl border border-gray-200 bg-secondary-extra-light px-4 py-3 outline-none focus:border-primary"
              />
              {nameError ? (
                <p className="mt-1 text-sm text-red-600" role="alert">{nameError}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="company-code" className="block font-semibold text-primary-dark mb-1">
                {t("companies.codeLabel")}
              </label>
              <input
                id="company-code"
                name="code"
                value={code}
                disabled={submitting}
                onChange={(event) => {
                  setCode(event.target.value.toUpperCase())
                  if (codeError) setCodeError("")
                }}
                className="w-full rounded-xl border border-gray-200 bg-secondary-extra-light px-4 py-3 outline-none focus:border-primary uppercase"
              />
              <p className="mt-1 text-sm text-gray-600">{t("companies.codeHelper")}</p>
              {codeError ? (
                <p className="mt-1 text-sm text-red-600" role="alert">{codeError}</p>
              ) : null}
            </div>

            <div className="flex flex-col items-end gap-3 pt-2">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="rounded-full border border-gray-300 bg-white px-5 py-2 font-semibold text-primary-dark disabled:opacity-60"
                >
                  {t("companies.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-full bg-primary px-5 py-2 font-semibold text-white disabled:opacity-60"
                >
                  {submitting ? t("companies.adding") : t("companies.add")}
                </button>
              </div>
              {formError ? (
                <p className="text-sm text-red-600" role="alert">{formError}</p>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
